import React from "react";
import { Wallet, TrendingUp, TrendingDown, DollarSign } from "lucide-react";
import { cn } from "../../utils/tailwindUtils";

const parseCurrency = (val) => {
    if (!val) return 0;
    if (typeof val === "number") return val;
    const cleaned = String(val).replace(/[^0-9.]/g, "");
    return parseFloat(cleaned) || 0;
};

const SalaryVisualizerCard = ({ data, compact }) => {
    if (!data || (!data.gross_earnings && !data.net_pay && !data.earnings_breakup)) {
        return null;
    }

    const gross = parseCurrency(data.gross_earnings);
    const deductions = parseCurrency(data.total_deductions);
    const net = parseCurrency(data.net_pay) || (gross - deductions);
    const maxVal = Math.max(gross, 1);

    const netPct = Math.min(100, Math.max(0, Math.round((net / maxVal) * 100)));
    const dedPct = Math.min(100, Math.max(0, Math.round((deductions / maxVal) * 100)));

    return (
        <div className="mt-3 overflow-hidden rounded-xl border border-border/80 bg-gradient-to-br from-card via-card to-muted/20 p-3.5 shadow-sm">
            {/* Header */}
            <div className="mb-3 flex items-center justify-between border-b border-border/60 pb-2">
                <div className="flex items-center gap-2">
                    <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                        <Wallet className="h-4 w-4" />
                    </div>
                    <div>
                        <h4 className="text-xs font-semibold text-foreground">Salary Visualizer</h4>
                        {data.period && (
                            <p className="text-[10px] text-muted-foreground">{data.period}</p>
                        )}
                    </div>
                </div>
                <div className="text-right">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Net Pay</p>
                    <p className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
                        {data.net_pay || `₹${net.toLocaleString('en-IN')}`}
                    </p>
                </div>
            </div>

            {/* Visual Bar */}
            <div className="mb-3 space-y-1.5">
                <div className="flex justify-between text-[11px] font-medium text-foreground">
                    <span>Earnings vs Deductions</span>
                    <span className="text-muted-foreground">{netPct}% Net Take-Home</span>
                </div>
                <div className="flex h-3.5 w-full overflow-hidden rounded-full bg-muted p-0.5 shadow-inner">
                    <div
                        className="h-full rounded-l-full bg-gradient-to-r from-emerald-500 to-teal-400 transition-all duration-500"
                        style={{ width: `${netPct}%` }}
                        title={`Net Pay: ${data.net_pay}`}
                    />
                    <div
                        className="h-full rounded-r-full bg-gradient-to-r from-rose-500 to-amber-500 transition-all duration-500"
                        style={{ width: `${dedPct}%` }}
                        title={`Deductions: ${data.total_deductions}`}
                    />
                </div>
                <div className="flex justify-between text-[10px] text-muted-foreground">
                    <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                        <span className="h-2 w-2 rounded-full bg-emerald-500" />
                        Net: {data.net_pay || `₹${net.toLocaleString('en-IN')}`}
                    </span>
                    <span className="flex items-center gap-1 text-rose-500">
                        <span className="h-2 w-2 rounded-full bg-rose-500" />
                        Deductions: {data.total_deductions || `₹${deductions.toLocaleString('en-IN')}`}
                    </span>
                </div>
            </div>

            {/* Metrics Breakdown Grid */}
            <div className="grid grid-cols-3 gap-2 border-t border-border/60 pt-2.5">
                <div className="rounded-lg bg-emerald-500/5 p-2 text-center border border-emerald-500/10">
                    <p className="flex items-center justify-center gap-1 text-[10px] text-emerald-700 dark:text-emerald-300">
                        <TrendingUp className="h-3 w-3" />
                        Gross
                    </p>
                    <p className="mt-0.5 text-xs font-bold text-foreground">
                        {data.gross_earnings || `₹${gross.toLocaleString('en-IN')}`}
                    </p>
                </div>

                <div className="rounded-lg bg-rose-500/5 p-2 text-center border border-rose-500/10">
                    <p className="flex items-center justify-center gap-1 text-[10px] text-rose-700 dark:text-rose-300">
                        <TrendingDown className="h-3 w-3" />
                        Deductions
                    </p>
                    <p className="mt-0.5 text-xs font-bold text-foreground">
                        {data.total_deductions || `₹${deductions.toLocaleString('en-IN')}`}
                    </p>
                </div>

                <div className="rounded-lg bg-sidebar/10 p-2 text-center border border-sidebar/20">
                    <p className="flex items-center justify-center gap-1 text-[10px] text-sidebar-primary dark:text-teal-300">
                        <DollarSign className="h-3 w-3" />
                        Net Pay
                    </p>
                    <p className="mt-0.5 text-xs font-bold text-emerald-600 dark:text-emerald-400">
                        {data.net_pay || `₹${net.toLocaleString('en-IN')}`}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default SalaryVisualizerCard;
